import { useState, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import SEOHead from '@/components/base/SEOHead';
import ScrollToTop from '@/components/base/ScrollToTop';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { videos, categories } from '@/mocks/videos';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [sortBy, setSortBy] = useState<'latest' | 'popular' | 'rating'>('latest');
  const [activeTab, setActiveTab] = useState<string>(slug || 'movie');

  const activeSlug = slug || activeTab;
  const category = categories.find(c => c.slug === activeSlug);

  const categoryVideos = useMemo(() => {
    let filtered = videos.filter(v => v.categorySlug === activeSlug);
    if (sortBy === 'popular') {
      filtered = [...filtered].sort((a, b) => b.views - a.views);
    } else if (sortBy === 'rating') {
      filtered = [...filtered].sort((a, b) => b.rating - a.rating);
    } else {
      filtered = [...filtered].sort((a, b) => new Date(b.publishDate).getTime() - new Date(a.publishDate).getTime());
    }
    return filtered;
  }, [activeSlug, sortBy]);

  const handleTabChange = (catSlug: string) => {
    setActiveTab(catSlug);
    window.scrollTo({ top: 0, behavior: 'instant' });
  };

  const pageSchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${category?.name || '视频'} - 91视频网站`,
    description: `91视频网站${category?.name || '视频'}分类，海量${category?.name || ''}资源免费在线观看，高清流畅播放`,
    url: `https://91video.example.com/category/${activeSlug}`,
    isPartOf: {
      '@type': 'WebSite',
      name: '91视频网站',
      url: 'https://91video.example.com',
    },
  };

  return (
    <>
      <SEOHead
        title={`${category?.name || '视频'} - 91视频网站 | 高清${category?.name || '视频'}免费在线观看`}
        description={`91视频网站${category?.name || '视频'}专区，海量${category?.name || '视频'}资源免费在线播放，高清流畅，更新及时，尽在91视频网站。`}
        keywords={`91视频网站,${category?.name || '视频'},91视频网站高清播放,91视频网站免费资源,视频在线播放平台`}
        canonicalUrl={`https://91video.example.com/category/${activeSlug}`}
        schema={pageSchema}
      />
      <ScrollToTop />
      <Navbar />
      <main className="min-h-screen pt-16 bg-background-50">
        <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: category?.name || '视频' },
            ]}
          />

          <div className="mb-8">
            <h1 className="text-2xl md:text-3xl font-heading text-foreground-900 mb-6">
              {category?.name || '视频'}
              <span className="text-base font-normal text-foreground-500 ml-3 font-sans">
                共 {categoryVideos.length} 部
              </span>
            </h1>

            <div className="flex flex-col sm:flex-row sm:items-center gap-4">
              <div className="flex items-center gap-2 flex-wrap" role="tablist" aria-label="视频分类">
                {categories.map((cat) => (
                  <Link
                    key={cat.slug}
                    to={`/category/${cat.slug}`}
                    onClick={(e) => { e.preventDefault(); handleTabChange(cat.slug); }}
                    role="tab"
                    aria-selected={activeSlug === cat.slug}
                    className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-200 ${
                      activeSlug === cat.slug
                        ? 'bg-primary-500 text-white'
                        : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                    }`}
                  >
                    {cat.name}
                  </Link>
                ))}
              </div>

              <div className="sm:ml-auto flex items-center gap-2 bg-background-100 rounded-full px-1 py-1" role="radiogroup" aria-label="排序方式">
                {[
                  { value: 'latest' as const, label: '最新' },
                  { value: 'popular' as const, label: '最热' },
                  { value: 'rating' as const, label: '评分' },
                ].map((option) => (
                  <button
                    key={option.value}
                    onClick={() => setSortBy(option.value)}
                    role="radio"
                    aria-checked={sortBy === option.value}
                    className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-200 ${
                      sortBy === option.value
                        ? 'bg-background-50 text-foreground-900'
                        : 'text-foreground-500 hover:text-foreground-700'
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5 pb-14" data-product-shop>
            {categoryVideos.map((video) => (
              <Link
                key={video.id}
                to={`/video/${video.id}`}
                className="group bg-background-50 rounded-xl overflow-hidden border border-background-200/60 hover:border-primary-200 transition-all duration-200"
              >
                <div className="relative aspect-video overflow-hidden">
                  <img
                    src={video.thumbnail}
                    alt={video.title}
                    title={`${video.title} - 91视频网站高清播放`}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-400"
                  />
                  <div className="absolute bottom-2 right-2 px-2 py-0.5 rounded-md bg-black/70 text-white text-xs">
                    {video.duration}
                  </div>
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-center justify-center">
                    <div className="w-12 h-12 flex items-center justify-center rounded-full bg-primary-500/90">
                      <i className="ri-play-fill text-white text-xl ml-0.5"></i>
                    </div>
                  </div>
                </div>
                <div className="p-3 md:p-4">
                  <h3 className="text-sm font-semibold text-foreground-900 line-clamp-2 group-hover:text-primary-600 transition-colors leading-snug">
                    {video.title}
                  </h3>
                  <div className="flex items-center gap-2 mt-2 text-xs text-foreground-500 flex-wrap">
                    <span className="flex items-center gap-1">
                      <i className="ri-eye-line"></i>
                      {video.views >= 10000 ? `${(video.views / 10000).toFixed(1)}万` : video.views.toLocaleString()}
                    </span>
                    <span className="flex items-center gap-1">
                      <i className="ri-star-fill text-accent-500"></i>
                      {video.rating}
                    </span>
                    <span className="text-foreground-400">{video.publishDate}</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}